import { useEffect, useState } from "react";
import { Pencil, Pill } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { fmt, fmtNum } from "../utils";
import { useLang } from "../i18n";
import UnitInput from "../components/UnitInput";

const empty = { name: "", hsn: "", supply_type: "Taxable", unit: "Strip", gst_rate: 12, manufacturer: "", company_id: "", mrp: 0, reorder_level: 10, barcode: "", manufacture_date: "", expiry_date: "" };

export default function Items() {
  const [rows, setRows] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [q, setQ] = useState("");
  const { t } = useLang();
  const load = async () => setRows((await api.get("/items", { params: { q } })).data);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load().catch(() => {}); }, [q]);
  useEffect(() => { api.get("/companies").then((r) => setCompanies(r.data)).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, gst_rate: Number(form.gst_rate), mrp: Number(form.mrp), reorder_level: Number(form.reorder_level), company_id: form.company_id || null };
    delete payload.manufacture_date;
    delete payload.expiry_date;
    try {
      if (editId) await api.put(`/items/${editId}`, payload);
      else await api.post("/items", payload);
      toast.success(editId ? "Item updated" : "Item added");
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (err) { toast.error(err.message); }
  };

  return (
    <div data-testid="items-page" className="sgp-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="sgp-page-title">{t("items")}</h1>
          <p className="sgp-page-subtitle">Item Master — reusable medicine/equipment details aur current stock record. Batch, MFG, expiry, qty aur purchase rate Purchase Bill se aate hain.</p>
        </div>
        <p className="text-xs text-slate-400">New items are created from New Purchase Bill; use the pencil here only to correct master details.</p>
      </div>

      <input data-testid="items-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search medicine by name..." className="sgp-input max-w-sm" />

      <div className="sgp-table-shell overflow-x-auto">
        <table className="w-full min-w-[860px]">
          <thead>
            <tr>
              <th className="sgp-th">Medicine</th><th className="sgp-th">HSN</th><th className="sgp-th">Unit</th>
              <th className="sgp-th">GST%</th><th className="sgp-th text-right">MRP</th>
              <th className="sgp-th text-right">Stock</th><th className="sgp-th text-right">Avg Cost</th><th className="sgp-th"></th>
            </tr>
          </thead>
          <tbody data-testid="items-table">
            {rows.map((it) => (
              <tr key={it.id} data-testid={`item-row-${it.id}`} className="sgp-table-row">
                <td className="sgp-td">
                  <div className="flex items-center gap-2.5">
                    <div className="h-8 w-8 rounded-md bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                      <Pill className="h-4 w-4 text-emerald-700" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-800">{it.name}</p>
                      <p className="text-xs text-slate-400">{it.manufacturer}{it.low_stock && <span className="ml-2 text-[10px] font-bold uppercase text-amber-600">Low stock</span>}</p>
                    </div>
                  </div>
                </td>
                <td className="sgp-td font-mono text-xs">{it.hsn}</td>
                <td className="sgp-td text-xs">{it.unit}</td>
                <td className="sgp-td font-mono text-xs">{it.gst_rate}%</td>
                <td className="sgp-td text-right font-mono text-xs">{fmt(it.mrp)}</td>
                <td className="sgp-td text-right font-mono font-semibold" data-testid={`item-stock-${it.id}`}>
                  <span className={it.low_stock ? "text-amber-600" : "text-slate-800"}>{fmtNum(it.stock)}</span>
                </td>
                <td className="sgp-td text-right font-mono text-xs">{fmt(it.avg_cost)}</td>
                <td className="sgp-td">
                  <button data-testid={`edit-item-${it.id}`} onClick={() => { setForm({ ...empty, ...it }); setEditId(it.id); setOpen(true); }}
                    className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-teal-700"><Pencil className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={8} className="sgp-td text-center py-10 text-slate-400">Koi item nahi mila. Pehle medicine master mein add karo.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="item-form-modal" className="max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editId ? "Edit Item" : "Add Medicine / Item"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-2 gap-3 mt-2">
            <div className="col-span-2"><label className="sgp-label">Item Name *</label>
              <input data-testid="item-name-input" required className="sgp-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><label className="sgp-label">HSN Code</label>
              <input data-testid="item-hsn-input" className="sgp-input font-mono" value={form.hsn} onChange={(e) => setForm({ ...form, hsn: e.target.value })} /></div>
            <div><label className="sgp-label">Unit / Type</label>
              <UnitInput testId="item-unit-input" value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} /></div>
            <div><label className="sgp-label">GST %</label>
              <select data-testid="item-gst-input" className="sgp-input" value={form.gst_rate} onChange={(e) => setForm({ ...form, gst_rate: e.target.value })}>
                {[0, 5, 12, 18, 28].map((g) => <option key={g} value={g}>{g}%</option>)}
              </select></div>
            <div><label className="sgp-label">Supply Tax Category</label><select data-testid="item-supply-type" className="sgp-input" value={form.supply_type || "Taxable"} onChange={e => setForm({ ...form, supply_type: e.target.value, gst_rate: e.target.value === "Taxable" ? form.gst_rate : 0 })}>{["Taxable", "Nil", "Exempt", "NonGST"].map(value => <option key={value} value={value}>{value === "NonGST" ? "Non-GST" : value}</option>)}</select></div>
            <div><label className="sgp-label">MRP (₹)</label>
              <input data-testid="item-mrp-input" type="number" step="0.01" className="sgp-input font-mono" value={form.mrp} onChange={(e) => setForm({ ...form, mrp: e.target.value })} /></div>
            <div><label className="sgp-label">Barcode (scan/search ke liye)</label>
              <input data-testid="item-barcode-input" className="sgp-input font-mono" value={form.barcode || ""} onChange={(e) => setForm({ ...form, barcode: e.target.value })} /></div>
            <div><label className="sgp-label">Manufacturer</label>
              <input data-testid="item-manufacturer-input" className="sgp-input" value={form.manufacturer} onChange={(e) => setForm({ ...form, manufacturer: e.target.value })} /></div>
            <div><label className="sgp-label">Reorder Level (alert)</label>
              <input data-testid="item-reorder-input" type="number" className="sgp-input font-mono" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} /></div>
            <p className="col-span-2 border-t pt-3 text-xs text-slate-500">Batch No., manufacturing date, expiry, quantity aur purchase rate item master ka part nahi hain — ye har Purchase Bill ke batch ke saath save hote hain.</p>
            <div className="col-span-2"><label className="sgp-label">Supplier Company (optional)</label>
              <select data-testid="item-company-input" className="sgp-input" value={form.company_id || ""} onChange={(e) => setForm({ ...form, company_id: e.target.value })}>
                <option value="">— Koi nahi —</option>
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select></div>
            <div className="col-span-2 flex justify-end gap-2 mt-1">
              <button type="button" data-testid="item-cancel-btn" onClick={() => setOpen(false)} className="px-4 py-2 text-sm rounded-md border border-slate-300 hover:bg-slate-50">{t("cancel")}</button>
              <button type="submit" data-testid="item-save-btn" className="sgp-primary-btn">{t("save")}</button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

